import { useState } from 'react';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import {
  useGetAllRepairCostings,
  useUpdateManyRepairCostings,
} from '@/hooks/repair-costing.hooks';
import { useNavigate } from 'react-router-dom';
import { RepairCostingTableView } from '../components/RepairCostingTableView';
import { TRepairCostingSchema } from '@/schemas/repair-costing/repair-costing.schema';
import { RepairCostingTableHeaders } from '../components/RepairCostingTableHeaders';
import { RepairCostingTableData } from '../components/RepairCostingTableData';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import Modal from '@/components/Modal';
import { useDeactivateField } from '@/hooks/deactivate-field.hooks';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { WarningMessage } from '@/components/Message/WarningMessage';

export const RepairCostingListPage = () => {
  const { t } = useTranslation('repairCosting');
  const navigate = useNavigate();
  const [totalActive, setTotalActive] = useState<number>(0);

  /**
   * === Deactivate Data section ===
   */
  const {
    numberOfItemsToDeactivate,
    pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
  } = useDeactivateField<TRepairCostingSchema>({
    t,
    useUpdateData: useUpdateManyRepairCostings,
  });

  const onGetTotalActive = (total: number) => {
    setTotalActive(total);
  };
  /**
   * === END OF Deactivate Data section ===
   */

  return (
    <>
      <PageHeader
        title={t('common:repairCostings')}
        totalItems={totalActive}
        headingElement={
          <PageInfoHeading text={t('repairCostingDescription')} />
        }
        actionListElement={
          <>
            <HeadingButton
              text={t('common:importAList')}
              leftIcon={
                <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-light-inverse dark-mode:[&>path]:fill-gray-600" />
              }
              onClick={() => navigate('#')}
            />
            <HeadingButton
              text={t('addRepairCosting')}
              leftIcon={
                <i className="-mt-[1.5px] ki-outline ki-plus-circle !text-[19.4px] text-light-inverse dark-mode:text-gray-600" />
              }
              onClick={() => navigate('/repair-costing/create')}
            />
          </>
        }
      />
      <div>
        <RepairCostingTableView<TRepairCostingSchema>
          t={t}
          useGetAllData={useGetAllRepairCostings}
          onGetTotalActive={onGetTotalActive}
          tableHeaders={RepairCostingTableHeaders}
          tableData={RepairCostingTableData}
          pendingDeactivate={pendingDeactivate}
          onDeactivate={handleDeactivate}
        />
      </div>
      <Modal
        id="information-modal"
        title={t('common:information')}
        centerY
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        size="small"
        bodyClassName="py-4"
      >
        <WarningMessage
          message={t('common:unableToDeactivateLinkedItem')}
          containerClassName="max-w-none"
        />
      </Modal>
      <ConfirmationModal
        id="deactivation-confirm-modal"
        title={t('common:confirmation')}
        description={
          numberOfItemsToDeactivate > 1 ? (
            <Trans
              i18nKey="common:confirmDeactivateItems"
              components={{
                br: <br />,
                span: <span className="font-medium" />,
              }}
            />
          ) : (
            <Trans
              i18nKey="common:confirmDeactivateItem"
              components={{ br: <br /> }}
            />
          )
        }
        onConfirmLabel={t('common:deactivate')}
        onConfirm={onConfirmDeactivate}
      />
    </>
  );
};
